/**
 * Keyboard activation for clickable table rows (SH-6).
 *
 * A `<tr onClick>` is unreachable without a mouse: rows are not focusable and
 * ignore Enter/Space. ACCESSIBILITY_MATRIX.md requires every action to be
 * keyboard operable, so a clickable row must also be focusable and activate on
 * Enter or Space.
 *
 *   // existing row: keep its onClick, add the two keyboard props
 *   <tr onClick={() => open(row)} tabIndex={0} onKeyDown={activateRowOnKey}>
 *
 *   // new code
 *   <TableRow {...rowActivationProps(() => open(row))}>
 *
 * Keys pressed inside a nested control (a checkbox, a button, an input) are
 * left to that control: only a key pressed while the row itself has focus
 * activates it.
 */
import type { KeyboardEvent } from 'react';

/** Focus ring + pointer for an activatable row. */
export const ACTIVATABLE_ROW_CLASS =
  'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-ring';

function isActivationKey(event: KeyboardEvent<HTMLElement>): boolean {
  return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
}

/**
 * onKeyDown for an element that already has an onClick: Enter/Space on the
 * element itself fires its click handler.
 */
export function activateRowOnKey(event: KeyboardEvent<HTMLElement>): void {
  if (event.target !== event.currentTarget || !isActivationKey(event)) return;
  event.preventDefault();
  event.currentTarget.click();
}

export interface RowActivationProps {
  tabIndex: 0;
  className: string;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/** Props that make a row clickable and keyboard operable. */
export function rowActivationProps(onActivate: () => void): RowActivationProps {
  return {
    tabIndex: 0,
    className: ACTIVATABLE_ROW_CLASS,
    onClick: onActivate,
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget || !isActivationKey(event)) return;
      event.preventDefault();
      onActivate();
    },
  };
}

/** Hook form of {@link rowActivationProps}. */
export const useRowActivation = rowActivationProps;
